import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';

// Helper envoi email : console.log en dev, vraie API en production
const sendEmail = async ({ to, subject, html }) => {
  const isDev = process.env.NODE_ENV === 'development';
  if (isDev) {
    console.log('%c📧 [DEV] Email simulé', 'color: #003189; font-weight: bold;');
    console.log('  → À :', to);
    console.log('  → Sujet :', subject);
    console.log('  → Corps HTML :', html);
    return;
  }
  await fetch('/api/send-email', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ to, subject, html })
  });
};

// ─── Templates email ──────────────────────────────────────────────────────────
const emailTemplateResponsable = ({ prenomNom, produitLabel, operateurNom, numeroVersion, lien }) => `
<!DOCTYPE html><html><head><meta charset="utf-8"></head>
<body style="margin:0;padding:0;background:#f4f4f4;font-family:Arial,sans-serif">
<table width="100%" cellpadding="0" cellspacing="0"><tr><td align="center" style="padding:40px 20px">
<table width="600" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:12px;overflow:hidden;box-shadow:0 4px 20px rgba(0,0,0,0.08)">
  <tr><td style="background:#003189;padding:24px 40px;text-align:center">
    <table cellpadding="0" cellspacing="0" style="margin:0 auto 16px"><tr>
      <td style="vertical-align:middle;padding-right:12px"><img src="https://factorylab3d.com/Image/Logo%20FFM%20blanc.png" alt="FFM" style="height:40px;display:block"></td>
      <td style="vertical-align:middle;padding-right:12px"><span style="color:rgba(255,255,255,0.7);font-size:13px;font-family:Arial,sans-serif">Custom Lab FFM by</span></td>
      <td style="vertical-align:middle"><img src="https://factorylab3d.com/Image/Logo%20Impulse%20blanc.svg" alt="Impulse" style="height:24px;display:block"></td>
    </tr></table>
    <h1 style="color:#ffffff;margin:0;font-size:22px;font-weight:700">Validation finale requise</h1>
    <p style="color:rgba(255,255,255,0.8);margin:8px 0 0;font-size:14px">L'opérateur FFM a approuvé ce BAT</p>
  </td></tr>
  <tr><td style="padding:40px">
    <p style="font-size:15px;color:#212529;margin:0 0 16px">Bonjour <strong>${prenomNom}</strong>,</p>
    <p style="font-size:15px;color:#495057;margin:0 0 24px;line-height:1.6">
      L'opérateur FFM <strong>${operateurNom}</strong> a validé le BAT <strong>version ${numeroVersion}</strong> pour :<br>
      <strong style="color:#003189">${produitLabel}</strong>
    </p>
    <p style="font-size:14px;color:#495057;margin:0 0 32px;line-height:1.6">
      Votre validation finale est maintenant requise pour lancer la production.
    </p>
    <table cellpadding="0" cellspacing="0"><tr><td style="background:#003189;border-radius:8px">
      <a href="${lien}" style="display:inline-block;padding:16px 32px;color:#ffffff;text-decoration:none;font-size:15px;font-weight:700">
        Valider le BAT →
      </a>
    </td></tr></table>
    <p style="font-size:12px;color:#adb5bd;margin:24px 0 0">Ce lien est personnel et à usage unique.</p>
  </td></tr>
  <tr><td style="background:#f8f9fa;padding:20px 40px;text-align:center;border-top:1px solid #e9ecef">
    <p style="font-size:12px;color:#adb5bd;margin:0">Fédération Française de Motocyclisme — Personnalisation des équipes de France</p>
  </td></tr>
</table></td></tr></table>
</body></html>`;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const isPdf = (url) => url?.toLowerCase().includes('.pdf');
const isImage = (url) => /\.(png|jpe?g|webp|gif|svg)(\?|$)/i.test(url || '');

// ─────────────────────────────────────────────────────────────────────────────

function FFMValidationBAT() {
  const { token } = useParams();

  const [etat, setEtat] = useState('chargement'); // chargement | valide | bloque | deja_traite | expiré | erreur
  const [tokenData, setTokenData] = useState(null);
  const [versionData, setVersionData] = useState(null);
  const [ligneData, setLigneData] = useState(null);
  const [commandeData, setCommandeData] = useState(null);

  const [action, setAction] = useState(null); // 'valider' | 'refuser'
  const [commentaire, setCommentaire] = useState('');
  const [fichiersRefus, setFichiersRefus] = useState([]); // fichiers joints au refus
  const [uploadingRefus, setUploadingRefus] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resultat, setResultat] = useState(null); // 'valide' | 'refuse'
  const [erreurAction, setErreurAction] = useState('');

  useEffect(() => {
    chargerToken();
  }, [token]); // eslint-disable-line

  const chargerToken = async () => {
    setEtat('chargement');
    try {
      // Charger le token avec toutes les données liées
      const { data: tok, error } = await supabase
        .from('ffm_bat_tokens')
        .select(`
          *,
          ffm_bat_versions (
            id, numero_version, fichier_url, statut,
            ffm_lignes_produit (
              id, produit_nom, option_valeur, statut,
              ffm_pilotes (prenom, nom),
              ffm_operateurs (prenom, nom, email),
              ffm_commandes (nom, nom_course)
            )
          )
        `)
        .eq('token', token)
        .single();

      if (error || !tok) { setEtat('erreur'); return; }

      const version = tok.ffm_bat_versions;
      const ligne = version?.ffm_lignes_produit;
      const commande = ligne?.ffm_commandes;

      setTokenData(tok);
      setVersionData(version);
      setLigneData(ligne);
      setCommandeData(commande);

      // Déterminer l'état
      if (tok.bloque) { setEtat('bloque'); return; }
      if (tok.statut !== 'en_attente') {
        setResultat(tok.statut);
        setEtat('deja_traite');
        return;
      }
      setEtat('valide');
    } catch (err) {
      console.error(err);
      setEtat('erreur');
    }
  };

  const handleAction = async (choix) => {
    if (choix === 'refuser' && !commentaire.trim()) {
      setErreurAction('Merci d\'indiquer la raison du refus.');
      return;
    }
    setErreurAction('');
    setSubmitting(true);

    try {
      // Upload des fichiers joints au refus si présents
      let urlsFichiersRefus = [];
      if (choix === 'refuser' && fichiersRefus.length > 0) {
        setUploadingRefus(true);
        for (const fichier of fichiersRefus) {
          const ext = fichier.name.split('.').pop();
          const path = `refus/${ligneData.id}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
          const { error: upErr } = await supabase.storage.from('ffm').upload(path, fichier, { upsert: false });
          if (!upErr) {
            const { data: urlData } = supabase.storage.from('ffm').getPublicUrl(path);
            if (urlData?.publicUrl) urlsFichiersRefus.push(urlData.publicUrl);
          }
        }
        setUploadingRefus(false);
      }

      const nouveauStatut = choix === 'valider' ? 'valide' : 'refuse';

      // 1. Mettre à jour le token
      await supabase
        .from('ffm_bat_tokens')
        .update({
          statut: nouveauStatut,
          commentaire_refus: choix === 'refuser' ? commentaire.trim() : null,
          pieces_jointes_refus: urlsFichiersRefus.length > 0 ? urlsFichiersRefus : [],
          date_action: new Date().toISOString()
        })
        .eq('token', token);

      const produitLabel = ligneData?.option_valeur
        ? `${ligneData.produit_nom} — ${ligneData.option_valeur}`
        : ligneData?.produit_nom || '';

      // ── Token OPÉRATEUR FFM (1ère validation) ─────────────────────────────
      if (tokenData.type === 'operateur') {
        const typeEvt = choix === 'valider' ? 'bat_valide_operateur' : 'bat_refuse_operateur';

        await supabase.from('ffm_bat_historique').insert([{
          ligne_produit_id: ligneData.id,
          version_id: versionData.id,
          type_evenement: typeEvt,
          auteur: tokenData.nom_destinataire,
          auteur_type: 'operateur_ffm',
          commentaire: commentaire.trim() || null,
          pieces_jointes: urlsFichiersRefus
        }]);

        if (choix === 'valider') {
          // Débloquer le token responsable + envoyer son email
          const { data: tokenResp } = await supabase
            .from('ffm_bat_tokens')
            .select('token, email, nom_destinataire')
            .eq('version_id', versionData.id)
            .eq('type', 'responsable')
            .maybeSingle();

          if (tokenResp?.token) {
            await supabase
              .from('ffm_bat_tokens')
              .update({ bloque: false })
              .eq('token', tokenResp.token);

            const baseUrl = window.location.origin;
            const lienResp = `${baseUrl}/ffm/bat/${tokenResp.token}`;
            await sendEmail({
              to: tokenResp.email,
              subject: `[FFM] Validation finale requise — ${produitLabel}`,
              html: emailTemplateResponsable({
                prenomNom: tokenResp.nom_destinataire,
                produitLabel,
                operateurNom: tokenData.nom_destinataire,
                numeroVersion: versionData.numero_version,
                lien: lienResp
              })
            });
          }
          // La ligne reste en bat_attente_validation (en attente du responsable)

        } else {
          // Opérateur refuse → bat_a_faire + invalider la version
          await supabase
            .from('ffm_lignes_produit')
            .update({ statut: 'bat_a_faire' })
            .eq('id', ligneData.id);
          await supabase
            .from('ffm_bat_versions')
            .update({ statut: 'refuse' })
            .eq('id', versionData.id);
        }

      // ── Token RESPONSABLE (validation finale) ─────────────────────────────
      } else if (tokenData.type === 'responsable') {
        const typeEvt = choix === 'valider' ? 'bat_valide_final' : 'bat_refuse_responsable';

        await supabase.from('ffm_bat_historique').insert([{
          ligne_produit_id: ligneData.id,
          version_id: versionData.id,
          type_evenement: typeEvt,
          auteur: tokenData.nom_destinataire,
          auteur_type: 'responsable_ffm',
          commentaire: commentaire.trim() || null,
          pieces_jointes: urlsFichiersRefus
        }]);

        if (choix === 'valider') {
          // Statut ligne → bat_valide
          await supabase
            .from('ffm_lignes_produit')
            .update({ statut: 'bat_valide' })
            .eq('id', ligneData.id);

          // Marquer la version comme validée
          await supabase
            .from('ffm_bat_versions')
            .update({ statut: 'valide' })
            .eq('id', versionData.id);

          // Vérifier si toutes les lignes de la commande sont bat_valide
          const { data: ligneAvecCommande } = await supabase
            .from('ffm_lignes_produit')
            .select('commande_id')
            .eq('id', ligneData.id)
            .single();

          if (ligneAvecCommande?.commande_id) {
            const { data: toutesLignes } = await supabase
              .from('ffm_lignes_produit')
              .select('statut')
              .eq('commande_id', ligneAvecCommande.commande_id);

            if (toutesLignes && toutesLignes.length > 0 && toutesLignes.every(l => l.statut === 'bat_valide')) {
              await supabase
                .from('ffm_commandes')
                .update({ statut: 'production_en_cours' })
                .eq('id', ligneAvecCommande.commande_id)
                .neq('statut', 'expediee');
            }
          }

        } else {
          // Responsable refuse → bat_a_faire + invalider la version
          await supabase
            .from('ffm_lignes_produit')
            .update({ statut: 'bat_a_faire' })
            .eq('id', ligneData.id);
          await supabase
            .from('ffm_bat_versions')
            .update({ statut: 'refuse' })
            .eq('id', versionData.id);
        }
      }

      setResultat(nouveauStatut);
      setEtat('deja_traite');
    } catch (err) {
      console.error(err);
      setErreurAction('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Rendu selon état ───────────────────────────────────────────────────────

  const produitLabel = ligneData?.option_valeur
    ? `${ligneData.produit_nom} — ${ligneData.option_valeur}`
    : ligneData?.produit_nom || '';

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f4f6f9', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 20px', fontFamily: 'Arial, sans-serif' }}>

      {/* Logo / En-tête */}
      <div style={{ marginBottom: '32px', textAlign: 'center' }}>
        <div style={{ backgroundColor: '#003189', borderRadius: '12px', padding: '10px 20px', display: 'inline-flex', alignItems: 'center', gap: '14px' }}>
          <img src="/Image/Logo FFM blanc.png" alt="FFM" style={{ height: '48px', objectFit: 'contain' }} />
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', fontWeight: '400' }}>Custom Lab by</span>
          <img src="/Image/Logo Impulse blanc.svg" alt="Impulse" style={{ height: '28px', objectFit: 'contain' }} />
        </div>
      </div>

      {/* ── Chargement ────────────────────────────────────────────────────── */}
      {etat === 'chargement' && (
        <div style={{ backgroundColor: '#ffffff', borderRadius: '16px', padding: '48px', textAlign: 'center', maxWidth: '480px', width: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize: '36px', marginBottom: '16px' }}>⏳</div>
          <p style={{ color: '#6c757d', fontSize: '15px' }}>Chargement en cours...</p>
        </div>
      )}

      {/* ── Erreur / Token invalide ───────────────────────────────────────── */}
      {etat === 'erreur' && (
        <Carte>
          <div style={{ fontSize: '36px', marginBottom: '16px', textAlign: 'center' }}>❌</div>
          <h2 style={stylesTitre}>Lien invalide ou expiré</h2>
          <p style={stylesTexte}>Ce lien de validation n'existe pas ou a expiré. Contactez votre responsable si vous pensez qu'il s'agit d'une erreur.</p>
        </Carte>
      )}

      {/* ── Token bloqué (opérateur attend pilote) ────────────────────────── */}
      {etat === 'bloque' && (
        <Carte>
          {versionData?.fichier_url && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
              {isPdf(versionData.fichier_url) ? (
                <iframe
                  src={versionData.fichier_url}
                  title="Aperçu BAT"
                  style={{ width: '100%', height: '320px', border: 'none', borderRadius: '8px', backgroundColor: '#f8f9fa' }}
                />
              ) : (
                <img src={versionData.fichier_url} alt="Aperçu BAT" style={{ maxWidth: '100%', maxHeight: '320px', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 2px 12px rgba(0,0,0,0.1)' }} />
              )}
            </div>
          )}
          {versionData?.fichier_url && (
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <a href={versionData.fichier_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: '#003189', fontWeight: '600', textDecoration: 'none' }}>
                Voir en pleine résolution →
              </a>
            </div>
          )}
          <h2 style={stylesTitre}>🔒 En attente de la validation de l'opérateur FFM</h2>
          <p style={stylesTexte}>
            Votre validation finale sera disponible dès que<br />
            l'opérateur FFM aura approuvé ce BAT.
          </p>
          <p style={{ ...stylesTexte, fontSize: '13px', color: '#adb5bd' }}>Vous recevrez un email automatique à ce moment-là.</p>
        </Carte>
      )}

      {/* ── Déjà traité ──────────────────────────────────────────────────── */}
      {etat === 'deja_traite' && (
        <Carte>
          {resultat === 'valide' ? (
            <>
              {versionData?.fichier_url && (
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                  {versionData.fichier_url.toLowerCase().includes('.pdf') ? (
                    <iframe src={versionData.fichier_url} title="BAT" style={{ width: '100%', height: '320px', border: 'none', borderRadius: '8px' }} />
                  ) : (
                    <img src={versionData.fichier_url} alt="BAT validé" style={{ maxWidth: '100%', maxHeight: '280px', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 2px 12px rgba(0,0,0,0.1)' }} />
                  )}
                </div>
              )}
              <h2 style={{ ...stylesTitre, color: '#155724' }}>BAT validé !</h2>
              <p style={stylesTexte}>
                {tokenData?.type === 'operateur' ? (
                  <>
                    Votre validation a bien été enregistrée.<br />
                    Le responsable FFM va effectuer la validation finale.
                  </>
                ) : 'La double validation est complète. Le BAT est officiellement approuvé et la production peut démarrer.'}
              </p>
              {tokenData?.type === 'responsable' && (
                <div style={{ backgroundColor: '#d4edda', borderRadius: '10px', padding: '16px', textAlign: 'center', marginTop: '16px' }}>
                  <strong style={{ color: '#155724', fontSize: '15px' }}>BAT validé définitivement ✓</strong>
                </div>
              )}
            </>
          ) : (
            <>
              {versionData?.fichier_url && (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                  {isPdf(versionData.fichier_url) ? (
                    <iframe
                      src={versionData.fichier_url}
                      title="BAT refusé"
                      style={{ width: '100%', height: '220px', border: 'none', borderRadius: '8px', backgroundColor: '#f8f9fa' }}
                    />
                  ) : (
                    <img src={versionData.fichier_url} alt="BAT refusé" style={{ maxWidth: '100%', maxHeight: '220px', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 2px 12px rgba(0,0,0,0.1)' }} />
                  )}
                </div>
              )}
              <h2 style={{ ...stylesTitre, color: '#721c24' }}>BAT refusé !</h2>
              <p style={stylesTexte}>
                Votre refus a bien été enregistré.<br />
                Une nouvelle version du BAT va être préparée.
              </p>
            </>
          )}
        </Carte>
      )}

      {/* ── Page principale de validation ────────────────────────────────── */}
      {etat === 'valide' && tokenData && versionData && (
        <div style={{ width: '100%', maxWidth: '800px' }}>

          {/* Carte infos commande */}
          <div style={{ backgroundColor: '#ffffff', borderRadius: '16px', padding: '24px 28px', marginBottom: '20px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>

            {/* Version — en premier, centré */}
            <div style={{ textAlign: 'center', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #e9ecef' }}>
              <span style={{ fontSize: '22px', fontWeight: '800', color: '#003189', letterSpacing: '0.5px' }}>
                Version {versionData.numero_version}
              </span>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h1 style={{ margin: '0 0 4px', fontSize: '20px', fontWeight: '800', color: '#212529' }}>
                  {ligneData?.ffm_pilotes
                    ? `${ligneData.ffm_pilotes.prenom} ${ligneData.ffm_pilotes.nom}`
                    : tokenData.nom_destinataire}
                </h1>
                <p style={{ margin: 0, fontSize: '14px', color: '#6c757d' }}>
                  {commandeData?.nom} — {commandeData?.nom_course}
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#adb5bd', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Produit</div>
                <div style={{ fontWeight: '800', color: '#003189', fontSize: '18px' }}>{produitLabel}</div>
              </div>
            </div>

            {/* Contexte selon type de token */}
            {tokenData.type === 'pilote' && (
              <div style={{ marginTop: '16px', backgroundColor: '#e8f0fe', borderRadius: '8px', padding: '12px 16px', fontSize: '13px', color: '#003189' }}>
                👁️ Cette maquette vous est transmise <strong>pour consultation</strong>. La validation est assurée par votre opérateur FFM.
              </div>
            )}
            {tokenData.type === 'operateur' && (
              <div style={{ marginTop: '16px', backgroundColor: '#fff3cd', borderRadius: '8px', padding: '12px 16px', fontSize: '13px', color: '#856404' }}>
                ⚡ <strong>1ère validation requise</strong> — Vous êtes l'opérateur FFM de cette ligne produit.
              </div>
            )}
            {tokenData.type === 'responsable' && (
              <div style={{ marginTop: '16px', backgroundColor: '#d4edda', borderRadius: '8px', padding: '12px 16px', fontSize: '13px', color: '#155724' }}>
                ✅ L'opérateur FFM a approuvé ce BAT. <strong>Votre validation finale est requise.</strong>
              </div>
            )}
          </div>

          {/* Visualisation du BAT */}
          <div style={{ backgroundColor: '#ffffff', borderRadius: '16px', padding: '24px 28px', marginBottom: '20px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <h2 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: '700', color: '#212529' }}>
              BAT — Version {versionData.numero_version}
            </h2>

            {isPdf(versionData.fichier_url) ? (
              <div>
                <iframe
                  src={versionData.fichier_url}
                  title="BAT"
                  style={{ width: '100%', height: '600px', border: 'none', borderRadius: '8px', backgroundColor: '#f8f9fa' }}
                />
                <a
                  href={versionData.fichier_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline-block', marginTop: '12px', color: '#003189', fontSize: '13px', fontWeight: '600', textDecoration: 'none' }}
                >
                  Ouvrir en plein écran →
                </a>
              </div>
            ) : isImage(versionData.fichier_url) ? (
              <div>
                <img
                  src={versionData.fichier_url}
                  alt="BAT"
                  style={{ width: '100%', borderRadius: '8px', border: '1px solid #e9ecef', display: 'block' }}
                />
                <a
                  href={versionData.fichier_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline-block', marginTop: '12px', color: '#003189', fontSize: '13px', fontWeight: '600', textDecoration: 'none' }}
                >
                  Voir en pleine résolution →
                </a>
              </div>
            ) : (
              <div style={{ backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '32px', textAlign: 'center' }}>
                <div style={{ fontSize: '36px', marginBottom: '12px' }}>📁</div>
                <p style={{ color: '#495057', margin: '0 0 16px', fontWeight: '600' }}>Fichier BAT disponible</p>
                <a
                  href={versionData.fichier_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline-block', padding: '12px 24px', backgroundColor: '#003189', color: '#ffffff', borderRadius: '8px', fontWeight: '700', textDecoration: 'none', fontSize: '14px' }}
                >
                  Télécharger le fichier
                </a>
              </div>
            )}
          </div>

          {/* Zone de décision */}
          <div style={{ backgroundColor: '#ffffff', borderRadius: '16px', padding: '28px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>

            {/* Pilote : consultation uniquement */}
            {tokenData.type === 'pilote' && (
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <div style={{ fontSize: '36px', marginBottom: '12px' }}>👁️</div>
                <h2 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: '700', color: '#212529' }}>Maquette disponible pour consultation</h2>
                <p style={{ margin: 0, fontSize: '14px', color: '#6c757d', lineHeight: '1.6' }}>
                  Cette maquette est en cours de traitement par votre opérateur FFM.<br />
                  Pour toute question, contactez directement votre équipe FFM.
                </p>
              </div>
            )}

            {/* Opérateur / Responsable : zone de validation */}
            {(tokenData.type === 'operateur' || tokenData.type === 'responsable') && (
              <>
                <h2 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: '700', color: '#212529' }}>Votre décision</h2>
                <p style={{ margin: '0 0 24px', fontSize: '14px', color: '#6c757d' }}>
                  {tokenData.type === 'operateur'
                    ? 'Examinez le BAT ci-dessus et effectuez votre 1ère validation.'
                    : 'Confirmez votre validation finale ou demandez des modifications.'}
                </p>

                {/* Boutons de sélection */}
                {action === null && (
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => setAction('valider')}
                      style={{ flex: 1, minWidth: '200px', padding: '18px', backgroundColor: '#003189', color: '#ffffff', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: '700', fontSize: '16px' }}
                    >
                      ✅ Valider le BAT
                    </button>
                    <button
                      onClick={() => setAction('refuser')}
                      style={{ flex: 1, minWidth: '200px', padding: '18px', backgroundColor: '#ffffff', color: '#dc3545', border: '2px solid #dc3545', borderRadius: '12px', cursor: 'pointer', fontWeight: '700', fontSize: '16px' }}
                    >
                      ↩️ Demander des modifications
                    </button>
                  </div>
                )}

                {/* Confirmation validation */}
                {action === 'valider' && (
                  <div style={{ backgroundColor: '#f0fff4', border: '1px solid #28a745', borderRadius: '12px', padding: '24px' }}>
                    <h3 style={{ margin: '0 0 12px', color: '#155724', fontSize: '16px' }}>Confirmer la validation</h3>
                    <p style={{ margin: '0 0 20px', color: '#495057', fontSize: '14px', lineHeight: '1.6' }}>
                      En validant, vous confirmez que le BAT <strong>version {versionData.numero_version}</strong> pour <strong>{produitLabel}</strong> est conforme à vos attentes.
                    </p>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => handleAction('valider')}
                        disabled={submitting}
                        style={{ padding: '14px 28px', backgroundColor: '#28a745', color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '15px', opacity: submitting ? 0.7 : 1 }}
                      >
                        {submitting ? 'Enregistrement...' : '✅ Oui, je valide ce BAT'}
                      </button>
                      <button
                        onClick={() => setAction(null)}
                        disabled={submitting}
                        style={{ padding: '14px 20px', backgroundColor: 'transparent', color: '#6c757d', border: '1px solid #dee2e6', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                )}

                {/* Formulaire refus */}
                {action === 'refuser' && (
                  <div style={{ backgroundColor: '#fff5f5', border: '1px solid #dc3545', borderRadius: '12px', padding: '24px' }}>
                    <h3 style={{ margin: '0 0 12px', color: '#721c24', fontSize: '16px' }}>Demande de modification</h3>
                    <p style={{ margin: '0 0 16px', color: '#495057', fontSize: '14px' }}>Expliquez les corrections souhaitées pour que nous puissions préparer une nouvelle version.</p>
                    <textarea
                      value={commentaire}
                      onChange={e => { setCommentaire(e.target.value); setErreurAction(''); }}
                      placeholder="Décrivez les modifications à apporter..."
                      style={{ width: '100%', minHeight: '120px', padding: '12px', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }}
                    />

                    {/* Upload fichiers d'annotation */}
                    <div style={{ marginTop: '16px' }}>
                      <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#495057', marginBottom: '8px' }}>
                        Fichiers joints (annotations, captures…) — optionnel
                      </label>
                      <label style={{
                        display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 18px',
                        backgroundColor: '#f0f4ff', border: '1.5px dashed #003189', borderRadius: '10px',
                        cursor: 'pointer', fontSize: '14px', color: '#003189', fontWeight: '600', transition: 'background-color 0.2s',
                      }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#e0e8ff'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = '#f0f4ff'}
                      >
                        <span style={{ fontSize: '20px' }}>📎</span>
                        <span>{fichiersRefus.length > 0 ? `${fichiersRefus.length} fichier(s) sélectionné(s)` : 'Joindre des fichiers'}</span>
                        <input type="file" multiple accept="image/*,application/pdf,.ai,.eps,.svg"
                          onChange={e => setFichiersRefus(Array.from(e.target.files))} style={{ display: 'none' }} />
                      </label>
                      {fichiersRefus.length > 0 && (
                        <ul style={{ margin: '10px 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {fichiersRefus.map((f, i) => (
                            <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#495057', backgroundColor: '#f8f9fa', padding: '6px 12px', borderRadius: '6px' }}>
                              <span>📄</span><span>{f.name}</span>
                              <span style={{ color: '#adb5bd', marginLeft: 'auto' }}>{(f.size / 1024).toFixed(0)} Ko</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {erreurAction && (
                      <div style={{ color: '#dc3545', fontSize: '13px', marginTop: '8px' }}>{erreurAction}</div>
                    )}
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '16px' }}>
                      <button
                        onClick={() => handleAction('refuser')}
                        disabled={submitting}
                        style={{ padding: '14px 28px', backgroundColor: '#dc3545', color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '15px', opacity: submitting ? 0.7 : 1 }}
                      >
                        {submitting ? (uploadingRefus ? 'Upload fichiers...' : 'Envoi...') : '↩️ Envoyer mes corrections'}
                      </button>
                      <button
                        onClick={() => { setAction(null); setCommentaire(''); setFichiersRefus([]); setErreurAction(''); }}
                        disabled={submitting}
                        style={{ padding: '14px 20px', backgroundColor: 'transparent', color: '#6c757d', border: '1px solid #dee2e6', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Composants UI locaux ─────────────────────────────────────────────────────
function Carte({ children }) {
  return (
    <div style={{ backgroundColor: '#ffffff', borderRadius: '16px', padding: '40px 36px', maxWidth: '480px', width: '100%', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
      {children}
    </div>
  );
}

const stylesTitre = { margin: '0 0 12px', fontSize: '20px', fontWeight: '700', color: '#212529', textAlign: 'center' };
const stylesTexte = { margin: '0 0 8px', fontSize: '14px', color: '#495057', lineHeight: '1.7', textAlign: 'center' };

export default FFMValidationBAT;
